// Pure helpers for turning a DevExtreme grid state into something safe to persist, and a persisted
// layout back into something safe to apply. Kept free of React and of the API service so the rules
// below are testable on their own — they are the part of saved layouts most likely to bite.
//
// The state shape these functions operate on is what DevExtreme 25.1.6's instance().state() actually
// returns, confirmed by probing a rendered grid:
//
//   { columns: [{ dataField, name, dataType, visible, visibleIndex, sortOrder, sortIndex, width, ... }],
//     allowedPageSizes, filterPanel: { filterEnabled }, filterValue, searchText, pageIndex, pageSize,
//     selectedRowKeys, selectionFilter?, focusedRowKey? }
//
// Note the keys are FLAT. The API reference lists the *configuration properties* state storing covers
// (paging.pageIndex, searchPanel.text), which reads like a nested shape but is not what you get back.

// Never persisted, and stripped again on the way in for layouts saved before this existed.
// selectedRowKeys / selectionFilter / focusedRowKey point at specific rows, which may since have been
// deleted or filtered away; pageIndex would drop the user onto a page that no longer holds the rows
// they saved the layout for. pageSize is kept — that is a preference, not a position.
// searchText is transient for the same reason: a search box is something you type to find one
// thing right now, so re-entering week-old text on apply reads as the grid filtering itself for
// no reason. Column filters are the durable half of "what am I looking at", and those are kept.
export const TRANSIENT_STATE_KEYS = Object.freeze([
  'selectedRowKeys',
  'selectionFilter',
  'focusedRowKey',
  'pageIndex',
  'searchText',
]);

function isStateObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function withoutTransientKeys(state) {
  const result = { ...state };
  TRANSIENT_STATE_KEYS.forEach((key) => {
    delete result[key];
  });
  return result;
}

// A layout's Data arrives as the JSON string the server stored. Corrupt or truncated data must not
// take the grid down while it is mounting, so anything unusable reads as "no layout" instead.
export function parseLayoutData(data) {
  if (isStateObject(data)) {
    return data;
  }

  if (typeof data !== 'string' || data.trim() === '') {
    return null;
  }

  try {
    const parsed = JSON.parse(data);
    return isStateObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Strips what must never be persisted, and by default strips column visibility too.
 *
 * DevExtreme only restores columns.visible when the grid has a ColumnChooser, and only
 * accounts-payable does. Persisting `visible` on the rest would store a value that can never be
 * restored, so it is dropped until a grid opts in via `includeColumnVisibility` — which makes
 * turning visibility on later purely additive.
 */
export function prepareLayoutStateForSave(state, { includeColumnVisibility = false } = {}) {
  const parsed = parseLayoutData(state);
  if (!parsed) {
    return null;
  }

  const prepared = withoutTransientKeys(parsed);

  if (Array.isArray(prepared.columns)) {
    prepared.columns = prepared.columns.map((column) => {
      const copy = { ...column };
      if (!includeColumnVisibility) {
        delete copy.visible;
      }
      return copy;
    });
  }

  return prepared;
}

/**
 * Prepares a persisted layout for instance().state(). Drops column entries the grid no longer has —
 * otherwise a layout saved before a column was renamed or removed reintroduces it as a phantom
 * column with an empty header — and reports them so the caller can tell the user what was ignored.
 *
 * Column filters are deliberately preserved: they are the main reason anyone saves a layout.
 * Search text is not — see TRANSIENT_STATE_KEYS.
 *
 * `knownFields` are the grid's current column identifiers. An empty list means the grid has not
 * reported its columns yet, in which case nothing is dropped: wiping every column would be a far
 * worse failure than leaving a stale one in place.
 */
export function sanitizeLayoutStateForApply(state, knownFields = []) {
  const parsed = parseLayoutData(state);
  if (!parsed) {
    return { state: null, droppedColumns: [] };
  }

  const sanitized = withoutTransientKeys(parsed);
  const droppedColumns = [];

  if (Array.isArray(sanitized.columns)) {
    if (knownFields.length === 0) {
      sanitized.columns = sanitized.columns.map((column) => ({ ...column }));
    } else {
      const known = new Set(knownFields);
      sanitized.columns = sanitized.columns.filter((column) => {
        // A column with neither identifier cannot be checked against anything, so it stays.
        const identifier = column.dataField ?? column.name;
        if (identifier === undefined || identifier === null) {
          return true;
        }

        if (known.has(identifier)) {
          return true;
        }

        droppedColumns.push(identifier);
        return false;
      });
    }
  }

  return { state: sanitized, droppedColumns };
}

/**
 * The column identifiers a grid currently has, for feeding to sanitizeLayoutStateForApply.
 *
 * Reads the configured columns rather than only the visible ones, so a column hidden through a
 * column chooser still counts as known — otherwise applying a layout would drop the entry for a
 * column the user has merely hidden. The visible columns are unioned in as well, because columns
 * added at runtime (or built by a customizeColumns handler) appear there but not in the option.
 *
 * An empty result means the grid has no columns yet, which is the signal for the sanitiser to leave
 * a saved layout's columns untouched.
 */
export function getKnownColumnIdentifiers(gridInstance) {
  if (!gridInstance) {
    return [];
  }

  const configured = typeof gridInstance.option === 'function' ? gridInstance.option('columns') : undefined;
  const visible = typeof gridInstance.getVisibleColumns === 'function' ? gridInstance.getVisibleColumns() : undefined;

  const identifiers = new Set();
  [configured, visible].forEach((columns) => {
    if (!Array.isArray(columns)) {
      return;
    }

    columns.forEach((column) => {
      const identifier = column?.dataField ?? column?.name;
      if (identifier !== undefined && identifier !== null) {
        identifiers.add(identifier);
      }
    });
  });

  return [...identifiers];
}
